import {Component, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {fieldError, fieldErrors, InvoicingService} from '../../shared/invoicing.service';
import {NotifierService} from '../../shared/notifier.service';
import {VatOption} from '../../interfaces/invoicing';

/** The tenant's VAT options (#1516), Admin only. A project picks one of them. */
@Component({
  selector: 'bst-vat-options',
  templateUrl: './vat-options.component.html',
  styles: [`
    .hint { color: var(--text-hint-color); font-size: 0.85rem; margin: 0 0 4px; }
    .vat-row { display: grid; grid-template-columns: minmax(10rem, 14rem) 1fr auto; align-items: center; gap: 0.5rem 1rem; min-height: 35px; padding: 0.25rem 0; border-bottom: 1px solid var(--border-basic-color-3); }
    .vat-row .printed { color: var(--text-hint-color); font-style: italic; overflow-wrap: anywhere; }
    .vat-row > :last-child { justify-self: end; }
    .vat-row .in-use { margin: 0; font-size: 0.75rem; }
    .vat-row button { padding-inline: 0.25rem 0; }
    .error { margin: 4px 0 0; font-size: 0.8rem; color: var(--color-danger-default); }
    .add-row { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; margin-top: 12px; }
    .add-row input { flex: 1; min-width: 14rem; max-width: 28rem; }
    @media (max-width: 767px) { .vat-row { grid-template-columns: 1fr auto; } .vat-row .printed { grid-row: 2; grid-column: 1 / -1; } }
    @media (max-width: 599px) { button.size-tiny { min-height: 44px; font-size: 0.8rem; } }
  `],
  standalone: false
})
export class VatOptionsComponent implements OnInit {
  rates: number[] = [];
  options: VatOption[] = [];
  draft: VatOption = this.blank();
  errors: Record<string, string[]> = {};
  busy = false;

  constructor(private invoicing: InvoicingService, private notifier: NotifierService, private translate: TranslateService) {}

  async ngOnInit() {
    [this.options, this.rates] = await Promise.all([this.invoicing.getVatOptions(), this.invoicing.getLists().then(l => l.vat_rates)])
      .catch(() => [[], []]);
  }

  // Typed text in the add row is lost on leaving; the selects alone are not input worth a warning.
  get isDirty(): boolean {
    return !!this.draft.text?.trim();
  }

  async add() {
    if (this.busy) { return; }
    this.busy = true;
    this.errors = {};
    const saved = await this.invoicing.saveVatOption(this.draft).catch(e => { this.errors = fieldErrors(e); return null; });
    this.busy = false;
    if (!saved) { return; }
    this.draft = this.blank();
    this.options = await this.invoicing.getVatOptions().catch(() => this.options);
  }

  async remove(option: VatOption) {
    if (this.busy) { return; }
    // The dialog shows its subtitle as given, so translate it first.
    const check = await this.translate.get('invoicing.vat.deletecheck').toPromise();
    if (!await this.notifier.confirm('invoicing.vat.confirm_delete', check, 'invoicing.vat.delete', 'danger', 'cancel')) { return; }
    this.busy = true;
    this.errors = {};
    const removed = await this.invoicing.removeVatOption(option.id).catch(e => { this.errors = fieldErrors(e); return null; });
    this.busy = false;
    if (removed) { this.options = this.options.filter(o => o.id !== option.id); }
  }

  errorList(): string[] {
    return Object.keys(this.errors).map(f => fieldError(this.translate, this.errors, f));
  }

  private blank(): VatOption {
    return {kind: 'standard', rate: 21, text: null};
  }
}
